import { useTranslation } from "@/hooks/useTranslation";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Globe, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

const SUPPORTED_LOCALES = [
  { code: 'ro', name: 'Română', flag: '🇷🇴', short: 'RO' },
  { code: 'en', name: 'English', flag: '🇬🇧', short: 'EN' },
];

interface LanguageSwitcherProps {
  compact?: boolean;
}

export function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { currentLanguage, changeLanguage } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLocale = SUPPORTED_LOCALES.find(loc => loc.code === currentLanguage) || SUPPORTED_LOCALES[0];

  const handleLanguageChange = (newLang: string) => {
    const currentPath = window.location.pathname;
    const pathWithoutLocale = currentPath.replace(/^\/(ro|en)/, '');
    
    // Change i18n language
    changeLanguage(newLang);
    
    // Update URL with new locale
    const newPath = `/${newLang}${pathWithoutLocale || ''}`;
    navigate(newPath);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <Button
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 h-auto py-1.5 px-2 hover:bg-muted text-muted-foreground"
          size="sm"
        >
          <Globe className="h-4 w-4" />
          <span className="text-xs font-medium">{currentLocale.short}</span>
        </Button>

        {isOpen && (
          <div className="absolute top-full mt-2 right-0 z-50 w-[140px] rounded-md border border-border bg-background shadow-lg">
            <div className="p-1">
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale.code}
                  onClick={() => handleLanguageChange(locale.code)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                    "hover:bg-muted hover:text-foreground",
                    "focus:bg-muted focus:text-foreground focus:outline-none",
                    currentLanguage === locale.code && "bg-muted text-foreground font-medium"
                  )}
                >
                  <span className="text-lg">{locale.flag}</span>
                  <span>{locale.name}</span>
                  {currentLanguage === locale.code && (
                    <span className="ml-auto text-xs text-primary">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 min-w-[140px] justify-between"
      >
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span>{currentLocale.name}</span>
        </div>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </Button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 z-50 w-[140px] rounded-md border border-border bg-background shadow-lg">
          <div className="p-1">
            {SUPPORTED_LOCALES.map((locale) => (
              <button
                key={locale.code}
                onClick={() => handleLanguageChange(locale.code)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                  "hover:bg-muted hover:text-foreground",
                  "focus:bg-muted focus:text-foreground focus:outline-none",
                  currentLanguage === locale.code && "bg-muted text-foreground font-medium"
                )}
              >
                <span className="text-lg">{locale.flag}</span>
                <span>{locale.name}</span>
                {currentLanguage === locale.code && (
                  <span className="ml-auto text-xs text-primary">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
